import { Colors } from "@blueprintjs/core";
import { useNavigate } from "react-router";

import { Flex } from "@/components/layout/flexbox";
import { MeetingScheduleButton } from "@/components/pages_logged_in/program/components/MeetingScheduleButton";
import { AllParticipantComponentTag } from "@/components/pages_logged_in/program/components/program-component/AllParticipantComponentTag";
import { ProgramComponentHeader } from "@/components/pages_logged_in/program/components/program-component/ProgramComponentHeader";
import { ProgramComponentStatusInfo } from "@/components/pages_logged_in/program/components/program-component/ProgramStatusInfo";
import { ProgramVideosAndArticlesItemPanel } from "@/components/pages_logged_in/program/components/program-component/ProgramVideosAndArticlesItemPanel";
import {
  OneOneIntroItemPanel,
  OneOneMeetingItemPanel,
  ProgramActionItemsPanel,
  ProgramAnnouncementItemPanel,
  ProgramGroupMeetingItemPanel,
  ProgramRoleplaysItemPanel,
  ProgramSurveyItemPanel,
} from "@/components/pages_logged_in/program/components/program-component/panels";
import { ScheduleTimeButton } from "@/components/pages_logged_in/program/components/ScheduleTimeButton";
import { CARD_CLASSES } from "@/css/constants";
import { ComponentTypeEnum } from "@/graphql";
import { cn } from "@/lib/utils";
import {
  isLeadComponent,
  LeadComponent,
  MainComponent,
  MainProgram,
  PanelProps,
  ParticipantComponent,
  ProgramRole,
} from "@/types/programs";

import { OneOneSessionItemPanel } from "./program-component/panels/one-one-coaching/OneOneSessionItemPanel";
import { allowEditComponent } from "./utils";

type ProgramComponentType = (
  | LeadComponent
  | ParticipantComponent
  | MainComponent
) & {
  meetings?: ParticipantComponent["meetings"];
};

const COMPONENT_ITEM_PANEL_MAP: Partial<
  Record<ComponentTypeEnum, React.ComponentType<PanelProps>>
> = {
  [ComponentTypeEnum.Survey]: ProgramSurveyItemPanel, // Supported isEligible
  [ComponentTypeEnum.ActionItem]: ProgramActionItemsPanel, // Supported isEligible
  [ComponentTypeEnum.Video]: ProgramVideosAndArticlesItemPanel, // Supported isEligible
  [ComponentTypeEnum.Article]: ProgramVideosAndArticlesItemPanel, // Supported isEligible
  [ComponentTypeEnum.Announcement]: ProgramAnnouncementItemPanel, // Not supported isEligible
  [ComponentTypeEnum.AiRoleplay]: ProgramRoleplaysItemPanel, // Supported isEligible
  [ComponentTypeEnum.GroupMeeting]: ProgramGroupMeetingItemPanel, // Not supported isEligible
  [ComponentTypeEnum.OneOneCoachingMaster]: OneOneMeetingItemPanel, // TODO @Long Supported isEligible
  [ComponentTypeEnum.OneOneCoachingIntro]: OneOneIntroItemPanel, // TODO @Long Supported isEligible
  [ComponentTypeEnum.OneOneCoachingSession]: OneOneSessionItemPanel, // TODO @Long Supported isEligible
};

interface ProgramComponentProps {
  component: ProgramComponentType;
  program: MainProgram;
  role?: ProgramRole;
  large?: boolean;
  collapsed?: boolean;
  mb?: number;
  view?: "list" | "feed";
  onEdit: (component?: ProgramComponentType) => void;
  onDelete: (component: ProgramComponentType) => void;
  onClose: (component: ProgramComponentType) => void;
  onClick?: () => void;
}

export function ProgramComponent({
  component,
  program,
  role = "participant",
  large = false,
  onEdit,
  onDelete,
  onClose,
  onClick,
  collapsed = false,
  mb = 24,
  view = "list",
}: ProgramComponentProps) {
  const navigate = useNavigate();
  const { type = ComponentTypeEnum.Survey, status } = component;

  const ComponentItemPanel = COMPONENT_ITEM_PANEL_MAP[type] || (() => null);
  const isSessionComponent = type === ComponentTypeEnum.OneOneCoachingSession;

  const isShowMeetingCalendarBtn =
    type === ComponentTypeEnum.GroupMeeting &&
    ["Upcoming", "Ongoing"].includes(status);

  const isProgramLead = role === "lead";
  const isProgramClosed = Boolean(program?.closedAt);

  const isEditable = allowEditComponent({
    component,
    isProgramClosed,
    isProgramLead,
  });

  const isShowScheduleTimeButton =
    [
      ComponentTypeEnum.Survey,
      ComponentTypeEnum.ActionItem,
      ComponentTypeEnum.AiRoleplay,
      ComponentTypeEnum.Video,
      ComponentTypeEnum.Article,
    ].includes(type) && !isProgramLead;

  if (isSessionComponent && !component.meetings?.length) {
    return null;
  }

  const componentId = `component_id_${component?.id}`;
  const onClickCapture = () => {
    navigate(`#${componentId}`, { replace: true });
  };

  const isShowInFeed = view === "feed";

  return (
    <Flex
      flex={1}
      mb={mb}
      className={cn(
        CARD_CLASSES,
        "xs:gap-3.5 xs:p-3.5 flex-col md:gap-4 md:p-6",
        {
          "cursor-pointer": !!onClick,
          "hover:!bg-blue-50": collapsed,
        },
      )}
      css={{ background: Colors.WHITE }}
      maxWidth={large ? undefined : 720}
      id={componentId}
      onClickCapture={onClickCapture}
      onClick={onClick}
    >
      <ProgramComponentHeader
        component={component}
        onEdit={onEdit}
        onDelete={onDelete}
        onClose={onClose}
        isEditable={isEditable}
        view={view}
        program={program}
      />

      {!collapsed && (
        <>
          {!isLeadComponent(component) && view !== "feed" && (
            <hr className="m-0 border-neutral-300" />
          )}
          {!isShowInFeed && (
            <ProgramComponentStatusInfo component={component} role={role} />
          )}

          <ComponentItemPanel
            component={component}
            program={program}
            role={role}
            large={large}
            onEdit={onEdit}
            view={view}
          />

          {!isShowInFeed && (isShowScheduleTimeButton || isProgramLead) && (
            <>
              {(isLeadComponent(component) ||
                isShowScheduleTimeButton ||
                isShowMeetingCalendarBtn) && (
                <hr className="m-0 border-neutral-300" />
              )}
              <div className="flex gap-2">
                {isLeadComponent(component) && (
                  <AllParticipantComponentTag
                    component={component}
                    programId={program?.id}
                    isProgramLead={isProgramLead}
                  />
                )}
                {isShowScheduleTimeButton && (
                  <ScheduleTimeButton component={component} />
                )}
                {isShowMeetingCalendarBtn && (
                  <MeetingScheduleButton
                    component={component}
                    program={program}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}
    </Flex>
  );
}
